import { useEffect, useState, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Button, Card, CardBody, Modal, ModalHeader, ModalBody, ModalFooter, Alert } from "reactstrap";
import SessionContext from "SessionContext";
import { LoadingDots } from "partials/progress-loader";

const ProcessUploadReview = (props) => {
    const context = useContext(SessionContext);
    const history = useHistory();

    const timeoutIds = useRef([]);

    const [uploadInfo, setUploadInfo] = useState();
    const [originalUploadInfo, setOriginalUploadInfo] = useState();
    const [headers, setHeaders] = useState();
    const [loaded, setLoaded] = useState(false);
    const [errorModalIsOpen, setErrorModalIsOpen] = useState(false);
    const [loadedFillReport, setLoadedFillReport] = useState(false);
    const [fieldReportWithLessThan100, setFieldReportWithLessThan100] = useState([]);
    const [badLinesInfo, setBadLinesInfo] = useState({});
    const [badLinesShowMessage, setBadLinesShowMessage] = useState(false);
    const [badLinesShowModal, setBadLinesShowModal] = useState(false);
    const [copyInvalidToClipboardSuccess, setCopyInvalidToClipboardSuccess] = useState(false);
    const [copyExtraToClipboardSuccess, setCopyExtraToClipboardSuccess] = useState(false);
    const [copyMissingToClipboardSuccess, setCopyMissingToClipboardSuccess] = useState(false);
    const [isWaitingForFillReport, setIsWaitingForFillReport] = useState(true);
    const [showConfirmUnder100Modal, setShowConfirmUnder100Modal] = useState(false);

    const getAllApiData = () => {
      context.query("uploads/get", { id: props.routeState.upload.id, }).then((res) => {
        setOriginalUploadInfo(props.routeState);
        setUploadInfo(res);
        setHeaders(res.preview_mapped[0]);
        if(res.preview_mapped && res.preview_mapped.length && res.preview_mapped[0].length) {
          res.preview_mapped.shift()
        }
        setLoaded(true);
      });
    }

    const getFillReportData = () => {
      context.query("uploads/get", { id: props.routeState.upload.id, }).then((res) => {
        if(res.fill_report && res.fill_report.length) {
          setUploadInfo({...uploadInfo, fill_report: res.fill_report, bad_lines: res.bad_lines, input_count: res.input_count});
          setLoadedFillReport(true);
          setIsWaitingForFillReport(false);
        } else {
          setLoadedFillReport(false);
          // keep trying every 10 seconds until the fill report comes back
          const id = setTimeout(getFillReportData, 10000);
          timeoutIds.current.push(id);
        }
      });
    }

    useEffect(() => {
      if(props && props.routeState) {
        getAllApiData();
      }
      return () => {
        timeoutIds.current.forEach(clearTimeout);
        timeoutIds.current = [];
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /**
     * @param _val a comma separated string
     * @returns a string without duplicated values
     */
    const removeDuplicatesLineNumbers = (_val) => {
      if(!_val || !_val.length) {return []}
      const valuesArray = _val.split(',');
      return [...new Set(valuesArray)].join(', ');
    }

    /**
     * @param _val a comma separated string
     * @returns the count of unique values
     */
    const countUniqueValues = (_val) => {
      if(!_val || !_val.length) {return 0}
      const valuesArray = _val.split(',');
      const uniqueValues = new Set(valuesArray);
      return uniqueValues.size;
    }

    useEffect(() => {
      let timer;
      if(!loadedFillReport) {
        if(uploadInfo) {
          if(uploadInfo.fill_report && uploadInfo.fill_report.length) {
            setLoadedFillReport(true);
            setIsWaitingForFillReport(false);
          } else {
            timer = setTimeout(() => {
              getFillReportData();
            }, 10000);
          }
        }
      } 
      
      // clear the timer when the component is unmounted
      return () => {
        if (timer) {
          clearTimeout(timer);
        }
      };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadInfo, loadedFillReport]);

    useEffect(() => {
      if(loadedFillReport) {
        let _lines = removeDuplicatesLineNumbers(uploadInfo.bad_lines.invalid_lines);
        let _extra = removeDuplicatesLineNumbers(uploadInfo.bad_lines.lines_with_extra_columns);
        let _missing = removeDuplicatesLineNumbers(uploadInfo.bad_lines.lines_with_missing_columns);
        // store all bad lines information
        let bad_lines_info = {
          invalid_lines: _lines,
          extra_columns: _extra,
          missing_columns: _missing,
          total_invalid_lines: countUniqueValues(_lines) || 0,
          total_extra_columns: countUniqueValues(_extra) || 0,
          total_missing_columns: countUniqueValues(_missing) || 0
        };
        setBadLinesInfo({...bad_lines_info});
        if(bad_lines_info.total_invalid_lines > 0 || bad_lines_info.total_extra_columns > 0 || bad_lines_info.total_missing_columns > 0) {
          setBadLinesShowMessage(true);
        }
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadedFillReport, uploadInfo])

    useEffect(() => {
      if(uploadInfo && uploadInfo.fill_report) {
        const fieldsNotRequired = [
          "prefix", "mname", "suffix", "addr2", "zip4", "company", "dpc", "crrt", "pmb", "dpv", "county", "addrtype", "urbanization", "casscode", "phone", "email", "gender", "dob", "latitude", "longitude"];
        let lessThan100 = [];
        uploadInfo.fill_report.forEach((item) => {
          if(item.populated_percentage !== "100.00%" && !fieldsNotRequired.includes(item.mapped_field)) {
            lessThan100.push(item.mapped_field);
          }
        })
        if(lessThan100.length) {
          setFieldReportWithLessThan100(lessThan100);
        } else {
          setFieldReportWithLessThan100([]);
        }
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadInfo])


    const onFinalizeUpload = (ev, allowUploadWith100OrLess=false) => {
      ev.stopPropagation();
      setShowConfirmUnder100Modal(false);
      let upload_data = originalUploadInfo;

      if(!Object.keys(upload_data).length) {
        setErrorModalIsOpen(true);
        return;
      }

      /* show modal if the file has less that 100 rows */
      let row_count = uploadInfo.input_count || 0;
      let hasLessThan100rows = Number(row_count) < 100;

      if(hasLessThan100rows && !allowUploadWith100OrLess) {
        setShowConfirmUnder100Modal(true);
        return;
      }

      const params = {
        upload_id: upload_data.upload.id,
        description: upload_data.description,
        mappings: upload_data.mappings,
        typings: upload_data.typings,
        waive_cass_ncoa: (hasLessThan100rows && allowUploadWith100OrLess) ? true : false
      };

      context.command("upload/process_upload", params, {}, { defaultErrorHandling: false }).then(() => {
        history.push("/upload-details/" + upload_data.upload.id)
      }).catch((err) => {
        console.log("Error Uploading File: ", JSON.stringify(err))
        setErrorModalIsOpen(true);
      })
    }

    const toggleModal = () => {
      setErrorModalIsOpen(!errorModalIsOpen)
    };

    // navigate back to the mapping page
    const handleBackToMapping = () => {
      history.push({
        pathname: `/process-upload/${originalUploadInfo.upload.id}`,
        state: originalUploadInfo
      });
    }

    const handleCopyLines = (_val, _row = "lines") => {
      navigator.clipboard.writeText(_val)
      .then(() => {
        if(_row === "lines") {
          setCopyInvalidToClipboardSuccess(true);
          setTimeout(() => {
            setCopyInvalidToClipboardSuccess(false);
          }, 2000);
        } else if(_row === "extras") {
          setCopyExtraToClipboardSuccess(true);
          setTimeout(() => {
            setCopyExtraToClipboardSuccess(false);
          }, 2000);
        } else if(_row === "missing") {
          setCopyMissingToClipboardSuccess(true);
          setTimeout(() => {
            setCopyMissingToClipboardSuccess(false);
          }, 2000);
        }
      })
      .catch((err) => {
        console.log('Failed to copy text: ' + err);
      });
    }

    const printModal = () => {
      // calculate the position to center the print window
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      const windowWidth = 900;
      const windowHeight = 800;
      const left = (screenWidth / 2) - (windowWidth / 2);
      const top = (screenHeight / 2) - (windowHeight / 2);
      // build and populate the content
      const printWindow = window.open('', '', `width=${windowWidth},height=${windowHeight},left=${left},top=${top}`);
      printWindow.document.write('<html><head><title>Print Issues Detected Details</title>');
      printWindow.document.write(`<style>
        body { font-family: Arial, sans-serif; margin: 2em; line-height: 1.6; }
        .close-button {
          display: block;
          margin: 20px auto;
          padding: 10px 20px;
          background-color: #666666;
          color: white;
          border: none;
          cursor: pointer;
          font-size: 16px;
        }
        @media print {
          .close-button {
            display: none;
          }
        }
      </style></head><body>`);
      printWindow.document.write(`<h2>Issues Detected Details</h2>`);
      printWindow.document.write(`<h3>File name: ${uploadInfo.filename}</h3>`);
      printWindow.document.write(`<div>
          Total Invalid line numbers: <strong>${badLinesInfo.total_invalid_lines}</strong> <br />
          Invalid line numbers: <strong>${badLinesInfo.invalid_lines}</strong>
        </div>`);
      printWindow.document.write(`<div>&nbsp;</div>`)
      printWindow.document.write(`<div>
          Total Lines with extra columns: <strong>${badLinesInfo.total_extra_columns}</strong> <br />
          Lines with extra columns: <strong>${badLinesInfo.extra_columns}</strong>
        </div>`);
      printWindow.document.write(`<div>&nbsp;</div>`)
      printWindow.document.write(`<div>
          Total Lines with missing columns: <strong>${badLinesInfo.total_missing_columns}</strong> <br />
          Lines with missing columns: <strong>${badLinesInfo.missing_columns}</strong>
        </div>`);
      printWindow.document.write('<br><button class="close-button" onclick="window.close();">Close</button>');
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.print();
    };

    return (
      <div className="page process-uploads-page">
        {!loaded ? "Loading..." : 
          (<>
            <div className="list-header sticky-page-header">
              <Button type="button" className="new-button" onClick={handleBackToMapping}>
                <i className="bi bi-chevron-left"></i>{" "}Return to Upload Mapping
              </Button>
              <h1>Upload Review</h1>
              <i className="spacer" />
            </div>
            <section className="mt-4">
              <h3 className="mb-3">Mapped File Preview: <strong>{uploadInfo.filename}</strong></h3>
              {headers && headers.length ? <Card className="preview">
                <CardBody className="scroll">
                  <div className="table-container">
                    <table className="styled fixed-header">
                    <thead>
                        <tr>
                        {headers.map((header, i) => (
                          <th className="file-header" key={i}>
                            {header}
                          </th>
                        ))}
                        </tr>
                    </thead>
                    <tbody>
                      {uploadInfo.preview_mapped &&
                        uploadInfo.preview_mapped.map((row, i) => (
                          <tr key={i}>
                            {row.map((cell, j) => (
                              <td key={j}>{cell}</td>
                            ))}
                          </tr>
                      ))}
                    </tbody>
                    </table>
                  </div>
                </CardBody>
              </Card> : <span>No Preview Available</span>}
            </section>

            <section className="mt-4">
              <h3 className="mb-3">Fill Report: <strong>{uploadInfo.filename}</strong></h3>
              {uploadInfo.fill_report && uploadInfo.fill_report.length ? <>
                <Card className="preview">
                  <CardBody className="scroll">
                    <div className="table-container">
                      <table className="styled fixed-header">
                      <thead>
                        <tr>
                          <th>Input Field Name</th>
                          <th>Mapped Field Name</th>
                          <th>Populated</th>
                          <th>% Populated</th>
                          <th>Null</th>
                          <th>% Null</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uploadInfo.fill_report.map((row, i) => (
                          <tr key={i}>
                            <td>{row.input_field}</td>
                            <td>{row.mapped_field}</td>
                            <td>{row.populated}</td>
                            <td>{row.populated_percentage}</td>
                            <td>{row.null_count}</td>
                            <td>{row.null_percentage}</td>
                          </tr>
                        ))}
                      </tbody>
                      </table>
                    </div>
                  </CardBody>
                </Card>
                {fieldReportWithLessThan100.length ?
                  <Alert color="warning" className="d-flex">
                    <i className="bi bi-lightbulb"></i>
                    <div>Note: The following fields are required for matching into the AudienceMaker dataset: 
                      {fieldReportWithLessThan100.map((item, index) => {
                        if (index === fieldReportWithLessThan100.length - 1) {
                          return (<strong key={item}>{` ${item}.`}</strong>);
                        } else {
                          return (<strong key={item}>{` ${item},`}</strong>);
                        }
                      })}
                      <br />Any records without this information will not be eligible for matching.</div>
                  </Alert> : ""}
              </> : <LoadingDots message="Generating Fill Report, this may take a few moments." />}
            </section>
            <section>
              {badLinesShowMessage ?
                <Alert color="warning" className="d-flex">
                  <i className="bi bi-exclamation-octagon"></i>
                  <div>
                  Warning: Issues were detected in the uploaded data that impact <strong>{badLinesInfo.total_invalid_lines}</strong> records. If you proceed, the impacted records will be removed and will not be uploaded. <br />
                  Click <button type="button" className="btn btn-link p-0 m-0 text-reset fw-bold" onClick={() => setBadLinesShowModal(true)}>HERE</button> to see impacted line numbers.
                  </div>
                </Alert>
              : ""}
            </section>

            <footer>
              <button onClick={onFinalizeUpload} disabled={isWaitingForFillReport}>Finalize Upload</button>
            </footer>
            <Modal centered backdrop isOpen={errorModalIsOpen} className="modal-lg">
              <ModalHeader>
                <i className="bi bi-exclamation-octagon"/> Error Uploading File
              </ModalHeader>
              <ModalBody>
                An Error Occurred. Please Try Uploading the File Again.
              </ModalBody>
              <ModalFooter>
                <Button onClick={() => toggleModal()}>Close</Button>
              </ModalFooter>
            </Modal>

            <Modal centered backdrop isOpen={showConfirmUnder100Modal} className="modal-lg">
              <ModalHeader>
                <i className="bi bi-exclamation-octagon"/> Less Than 100 Records
              </ModalHeader>
              <ModalBody>
                Warning: Your Upload contains less than 100 records and cannot be run through CASS and NCOA. If you proceed, the uploaded records will not be run through CASS and NCOA.<br />Contact Customer Success for more details.
              </ModalBody>
              <ModalFooter>
                <Button onClick={(ev) => onFinalizeUpload(ev, true)}>Proceed</Button>
                <Button className="promote" onClick={() => setShowConfirmUnder100Modal(false)}>Cancel</Button>
              </ModalFooter>
            </Modal>

            <Modal centered backdrop isOpen={badLinesShowModal} className="modal-lg">
              <ModalHeader>
                <i className="bi bi-exclamation-octagon"/> Issues Detected Details
              </ModalHeader>
              <ModalBody>
                <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                  <div className="m-2 mb-4">
                    Total Invalid line numbers: <strong>{badLinesInfo.total_invalid_lines}</strong> <br />
                    {copyInvalidToClipboardSuccess ? <span><i className="bi bi-check" />Copied To Clipboard</span> : <>
                      <i className="bi bi-clipboard cursor-pointer" onClick={() => handleCopyLines(badLinesInfo.invalid_lines, "lines")} title="Copy List To Clipboard"/>
                      Invalid line numbers: 
                    </>} <strong>{badLinesInfo.invalid_lines}</strong>
                  </div>
                  <div className="m-2 mb-4">
                    Total Lines with extra columns: <strong>{badLinesInfo.total_extra_columns}</strong> <br />
                    {copyExtraToClipboardSuccess ? <span><i className="bi bi-check" />Copied To Clipboard</span> : <>
                      <i className="bi bi-clipboard cursor-pointer" onClick={() => handleCopyLines(badLinesInfo.extra_columns, "extras")} title="Copy List To Clipboard"/>
                      Lines with extra columns: 
                    </>} <strong>{badLinesInfo.extra_columns}</strong>
                  </div>
                  <div className="m-2 mb-4">
                    Total Lines with missing columns: <strong>{badLinesInfo.total_missing_columns}</strong> <br />
                    {copyMissingToClipboardSuccess ? <span><i className="bi bi-check" />Copied To Clipboard</span> : <>
                      <i className="bi bi-clipboard cursor-pointer" onClick={() => handleCopyLines(badLinesInfo.missing_columns, "missing")} title="Copy List To Clipboard"/>
                      Lines with missing columns:
                    </>} <strong>{badLinesInfo.missing_columns}</strong>
                  </div>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button className="btn btn-primary" onClick={printModal}>Print Details</Button>
                <Button onClick={() => setBadLinesShowModal(false)}>Close</Button>
              </ModalFooter>
            </Modal>
          </>)
        }
      </div>
    )
}

export default ProcessUploadReview;